import * as Yup from "yup";
import { Formik } from "formik";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addSingleStudent, editStudent } from "@/api/students";
import { CustomCombobox, CustomInput, CustomSelect } from "@/components";
import { Button } from "@/components/ui/button";
import { showSuccessToast } from "@/lib/toast";
import { Student } from "@/types/user.types";
import { Loader2Icon } from "lucide-react";
import { useMainLayoutContext } from "@/context/MainLayoutContext";

type AddSingleStudentProps = {
  currentStudent?: Student;
  classId?: string,
  onClose?: () => void;
};

const AddSingleStudent = ({
  currentStudent,
  onClose,
  classId
}: AddSingleStudentProps) => {
  const queryClient = useQueryClient();
  const { classes } = useMainLayoutContext();

  const initialValues = currentStudent
    ? {
      studentID: currentStudent.id,
      username: currentStudent.username || "",
      firstname: currentStudent.firstname || "",
      surname: currentStudent.surname || "",
      othernames: currentStudent.othernames || "",
      email: currentStudent.email || "",
      mobile: currentStudent.mobile || "",
      sex: currentStudent.sex || "",
      classId: currentStudent.class_id || "",
    }
    : {
      firstname: "",
      username: "",
      surname: "",
      othernames: "",
      email: "",
      mobile: "",
      sex: "",
      classId: classId ?? "",
    };

  const validationSchema = Yup.object().shape({
    surname: Yup.string().required("Student's surname is required"),
    firstname: Yup.string().required("Student's firstname is required"),
    othernames: Yup.string(),
    classId: Yup.string().required("Select student's class"),
    sex: Yup.string()
      .required("Student's sex is required")
      .oneOf(["male", "female"], "Gender must be either male or female"),
    email: Yup.string().email(),
    mobile: Yup.string(),
  });

  const { mutate, isLoading } = useMutation({
    mutationFn: currentStudent?.id ? editStudent : addSingleStudent,
    onSuccess: () => {
      showSuccessToast(currentStudent?.id ? "Student updated successfully" : "Student added successfully");

      queryClient.invalidateQueries({ queryKey: ["students"] });
      onClose?.();
    },
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => mutate(values)}
    >
      {({ values, handleChange, handleSubmit, setFieldValue }) => (
        <form
          className="flex flex-col justify-between gap-4 pt-6"
          onSubmit={handleSubmit}
        >
          <CustomInput
            label="Admission number"
            id="username"
            value={values.username}
            onChange={handleChange}
            placeholder="Enter username"
            name="username"
          />
          <CustomInput
            label="Surname"
            id="surname"
            value={values.surname}
            onChange={handleChange}
            placeholder="Enter surname"
            name="surname"
          />
          <CustomInput
            label="First Name"
            value={values.firstname}
            id="firstname"
            onChange={handleChange}
            placeholder="Enter first name"
            name="firstname"
          />
          <CustomInput
            label="Other Name"
            id="othernames"
            value={values.othernames}
            onChange={handleChange}
            placeholder="Enter other names"
            name="othernames"
          />
          <CustomSelect
            label="Gender"
            value={values.sex}
            options={[
              { label: "Male", value: "male" },
              { label: "Female", value: "female" },
            ]}
            onChange={(val) => setFieldValue("sex", val)}
            placeholder="Select sex"
            name="sex"
          />
          <CustomCombobox
            label="Class"
            value={values.classId}
            options={classes?.map(({ id, class: className }) => ({
              value: id,
              label: className,
            })) ?? []}
            onChange={(val) => setFieldValue("classId", val)}
            placeholder="Select class"
            id="classId"
            name="classId"
          />
          <CustomInput
            label="Email"
            id="email"
            type="email"
            value={values.email}
            onChange={handleChange}
            disabled={!!currentStudent?.id}
            placeholder="Enter email"
            name="email"
          />
          <CustomInput
            label="Phone"
            id="mobile"
            type="tel"
            value={values.mobile}
            onChange={handleChange}
            placeholder="Enter phone number"
            name="mobile"
          />

          <div className="flex items-center justify-end w-full gap-2">
            <Button
              variant="outline"
              type="button"
              onClick={() => onClose?.()}
            >
              Cancel
            </Button>
            <Button type="submit" className="min-w-[100px]">
              {isLoading ? <Loader2Icon className="animate-spin" /> : "Save"}
            </Button>
          </div>
        </form>
      )}
    </Formik>
  );
};

export default AddSingleStudent;
